import type { Timesheet } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import {
  DUE_STATUS_LABELS,
  DUE_STATUS_TONES,
  SUBMISSION_TIMING_LABELS,
  SUBMISSION_TIMING_TONES,
} from './status.js';

/** Formats a due deadline (ISO datetime) compactly in the viewer's local timezone. */
export function formatDueAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface DueIndicatorProps {
  timesheet: Pick<Timesheet, 'submissionTiming' | 'dueStatus' | 'dueAt'>;
  /** When false, the deadline date underneath the badge is hidden. */
  showDate?: boolean;
}

/** True when the ISO deadline falls on the viewer's local calendar day. */
function isDueToday(iso: string): boolean {
  const due = new Date(iso);
  const now = new Date();
  return (
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate()
  );
}

/**
 * Renders a timesheet's due indicator: a submitted timesheet shows its frozen on-time /
 * past-due flag; an unsubmitted one shows the live not-due / due-this-week / overdue status.
 * Renders an em dash when no due date is configured.
 */
export function DueIndicator({ timesheet, showDate = true }: DueIndicatorProps): JSX.Element {
  const tone = timesheet.submissionTiming
    ? SUBMISSION_TIMING_TONES[timesheet.submissionTiming]
    : timesheet.dueStatus
      ? DUE_STATUS_TONES[timesheet.dueStatus]
      : null;
  const label = timesheet.submissionTiming
    ? SUBMISSION_TIMING_LABELS[timesheet.submissionTiming]
    : timesheet.dueStatus === 'due_this_week' && timesheet.dueAt && isDueToday(timesheet.dueAt)
      ? 'Due Today'
      : timesheet.dueStatus
        ? DUE_STATUS_LABELS[timesheet.dueStatus]
        : null;
  if (!tone || !label) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex flex-col gap-1">
      <Badge tone={tone}>{label}</Badge>
      {showDate && timesheet.dueAt ? (
        <span className="text-xs text-slate-400">{formatDueAt(timesheet.dueAt)}</span>
      ) : null}
    </div>
  );
}
